import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import { Plus, XClose } from '@untitledui/icons';
import { type FC, useCallback, useMemo, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { Input } from '@/lib/base/forms/input/input';
import { Select, type SelectItemType } from '@/lib/base/forms/select/select';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import type {
  BookingQuestion,
  BookingQuestionType,
} from '@/lib/crm/utils/bookingQuestions';
import {
  BOOKING_QUESTION_MAX,
  generateQuestionKey,
  isMappableType,
  isSelectType,
  parseBookingQuestions,
  QUESTION_TYPE_OPTIONS,
  serializeBookingQuestions,
} from '@/lib/crm/utils/bookingQuestions';
import { useMultiValueSensors } from '@/lib/crm/utils/multiValueDnd';
import { cx } from '@/utils/cx';

import { SelectInputError, SelectInputLoading } from './SelectInputStates';
import type { PropertyInputProps } from './TextInput';

// Mutable copy required because Select.items expects a mutable array; the
// source QUESTION_TYPE_OPTIONS is a const ReadonlyArray.
const QUESTION_TYPE_ITEMS: SelectItemType[] = QUESTION_TYPE_OPTIONS.map(o => ({
  id: o.id,
  label: o.label,
}));

// ─── OptionInput ─────────────────────────────────────────────────────────────

interface OptionInputProps {
  index: number;
  value: string;
  disabled: boolean;
  /** Field size for the option text input, on the shared library scale. */
  size: SelectSize;
  onChangeOption: (index: number, value: string) => void;
  onRemoveOption: (index: number) => void;
}

const OptionInput: FC<OptionInputProps> = ({
  index,
  value,
  disabled,
  size,
  onChangeOption,
  onRemoveOption,
}) => {
  const handleChange = useCallback(
    (val: string) => onChangeOption(index, val),
    [index, onChangeOption]
  );

  const handleRemove = useCallback(
    () => onRemoveOption(index),
    [index, onRemoveOption]
  );

  return (
    <div className='flex items-center gap-2'>
      <div className='flex-1'>
        <Input
          size={size}
          value={value}
          onChange={handleChange}
          isDisabled={disabled}
          aria-label={`Option ${index + 1}`}
          placeholder={`Option ${index + 1}`}
        />
      </div>
      {!disabled && (
        <Button
          size='sm'
          color='tertiary'
          iconLeading={XClose}
          onClick={handleRemove}
          aria-label={`Remove option ${index + 1}`}
        />
      )}
    </div>
  );
};

// ─── ContactPropertyPicker ───────────────────────────────────────────────────

interface ContactPropertyPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
  /** Field size for the picker, on the shared library scale. */
  size: SelectSize;
}

const ContactPropertyPicker: FC<ContactPropertyPickerProps> = ({
  value,
  onChange,
  disabled,
  size,
}) => {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const { data, isLoading, error } = useQuery({
    queryKey: ['contact-property-types-bindable', orgId],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }
      const client = createSchemaClient(orgId);
      const [propertyTypes, fieldTypes] = await Promise.all([
        client.getPropertyTypes('ahoy_contact'),
        client.getFieldTypes(),
      ]);
      return { propertyTypes, fieldTypes };
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const items = useMemo(() => {
    if (!data) {
      return [];
    }
    // Booking answers are string values; the backend only binds them to
    // string-typed properties (skipping others with a `property_not_string`
    // warning). Mirror that eligibility here so hosts can't save a mapping
    // that silently no-ops at booking time.
    const stringFieldTypeNames = new Set(
      data.fieldTypes
        .filter(ft => ft.value_type_name === 'string')
        .map(ft => ft.field_type_name)
    );
    return data.propertyTypes
      .filter(
        pt =>
          pt.entity_type_name === 'ahoy_contact' &&
          pt.value_write_access === 'all' &&
          stringFieldTypeNames.has(pt.field_type_name)
      )
      .map(pt => ({ id: pt.property_type_name, label: pt.label }));
  }, [data]);

  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      onChange(typeof key === 'string' && key ? key : null);
    },
    [onChange]
  );

  const handleClear = useCallback(() => onChange(null), [onChange]);

  if (isLoading) {
    return <SelectInputLoading />;
  }

  if (error) {
    return <SelectInputError error={error} />;
  }

  return (
    <div className='flex items-center gap-2'>
      <div className='flex-1'>
        <Select.ComboBox
          placeholder={
            items.length === 0
              ? 'No writable contact properties'
              : 'Select a contact property'
          }
          size={size}
          items={items}
          selectedKey={value}
          onSelectionChange={handleSelectionChange}
          isDisabled={disabled || items.length === 0}
          shortcut={false}
          aria-label='Contact property to save answer to'
        >
          {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select.ComboBox>
      </div>
      {!disabled &&
        (value ? (
          <Button
            size='sm'
            color='tertiary'
            iconLeading={XClose}
            onClick={handleClear}
            aria-label='Clear contact property mapping'
          />
        ) : (
          // Reserve the clear-button footprint (sm icon-only Button = size-9)
          // so the picker's right edge stays aligned with the option inputs
          // and the filled-picker state instead of stretching wider when empty.
          <div className='size-9 shrink-0' aria-hidden='true' />
        ))}
    </div>
  );
};

// ─── QuestionRow ─────────────────────────────────────────────────────────────

interface QuestionRowProps {
  question: BookingQuestion;
  index: number;
  disabled: boolean;
  /** Focus the label input on mount — set for the row just added via "Add question". */
  autoFocusLabel: boolean;
  /** Field size for the row's inputs and selects, on the shared library scale. */
  size: SelectSize;
  onChangeQuestion: (key: string, patch: Partial<BookingQuestion>) => void;
  onRemove: (key: string) => void;
}

const QuestionRow: FC<QuestionRowProps> = ({
  question,
  index,
  disabled,
  autoFocusLabel,
  size,
  onChangeQuestion,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.key, disabled });

  // Parallel array of synthetic ids, index-aligned with question.options, used
  // only as the React key on OptionInput — never merged into the emitted value.
  const [optionIds, setOptionIds] = useState<string[]>(() =>
    question.options.map(() => crypto.randomUUID())
  );

  // Transform/transition must be applied as inline styles — required by dnd-kit
  // for the drag animation to work (values are computed dynamically by the library).
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleLabelChange = useCallback(
    (val: string) => onChangeQuestion(question.key, { label: val }),
    [question.key, onChangeQuestion]
  );

  const handleTypeChange = useCallback(
    (selectedKey: string | number | null) => {
      if (typeof selectedKey !== 'string') {
        return;
      }
      const newType = selectedKey as BookingQuestionType;
      const patch: Partial<BookingQuestion> = { type: newType };
      if (!isSelectType(newType)) {
        // Clear options when switching away from a select type
        // (options-iff-select invariant).
        patch.options = [];
        setOptionIds([]);
      } else if (question.options.length === 0) {
        // Switching TO a select type: seed one empty option input so the
        // host lands on an editable field instead of an "Add at least one
        // option" error next to a bare "Add option" button.
        patch.options = [''];
        setOptionIds([crypto.randomUUID()]);
      }
      onChangeQuestion(question.key, patch);
    },
    [question.key, question.options.length, onChangeQuestion]
  );

  const handleRequiredChange = useCallback(
    (required: boolean) => onChangeQuestion(question.key, { required }),
    [question.key, onChangeQuestion]
  );

  const handleMapToPropertyChange = useCallback(
    (key: string | null) =>
      onChangeQuestion(question.key, {
        map_to_property: typeof key === 'string' && key ? key : undefined,
      }),
    [question.key, onChangeQuestion]
  );

  const handleRemove = useCallback(
    () => onRemove(question.key),
    [question.key, onRemove]
  );

  const handleOptionChange = useCallback(
    (optIndex: number, val: string) => {
      const nextOptions = question.options.map((opt, i) =>
        i === optIndex ? val : opt
      );
      onChangeQuestion(question.key, { options: nextOptions });
    },
    [question.key, question.options, onChangeQuestion]
  );

  const handleAddOption = useCallback(() => {
    setOptionIds(prev => [...prev, crypto.randomUUID()]);
    onChangeQuestion(question.key, {
      options: [...question.options, ''],
    });
  }, [question.key, question.options, onChangeQuestion]);

  const handleRemoveOption = useCallback(
    (optIndex: number) => {
      setOptionIds(prev => prev.filter((_, i) => i !== optIndex));
      onChangeQuestion(question.key, {
        options: question.options.filter((_, i) => i !== optIndex),
      });
    },
    [question.key, question.options, onChangeQuestion]
  );

  const labelError = question.label.trim().length === 0;
  const optionsError =
    isSelectType(question.type) && question.options.length === 0;
  const atOptionMax = question.options.length >= BOOKING_QUESTION_MAX.OPTIONS;
  const labelErrorId = `bq-label-error-${question.key}`;
  const optionsErrorId = `bq-options-error-${question.key}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cx(
        'rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-3 space-y-3',
        isDragging && 'opacity-40'
      )}
    >
      {/* Row header: drag handle, label input, type select, required checkbox, remove */}
      <div className='flex flex-wrap items-start gap-2'>
        {!disabled && (
          <button
            type='button'
            {...listeners}
            {...attributes}
            className='mt-1 flex items-center justify-center touch-none rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
            aria-label='Drag to reorder question'
          >
            <GripVertical className='size-4 text-[var(--color-text-tertiary)]' />
          </button>
        )}

        <div className='flex-1 min-w-32'>
          <Input
            size={size}
            value={question.label}
            onChange={handleLabelChange}
            isDisabled={disabled}
            aria-label='Question label'
            isInvalid={labelError}
            aria-describedby={labelError ? labelErrorId : undefined}
            placeholder='Question label'
            autoFocus={autoFocusLabel}
          />
          {labelError && (
            <p
              id={labelErrorId}
              className='mt-1 text-xs text-[var(--color-text-error-primary)]'
            >
              Label is required
            </p>
          )}
        </div>

        <div className='w-36 shrink-0'>
          <Select
            size={size}
            items={QUESTION_TYPE_ITEMS}
            selectedKey={question.type}
            onSelectionChange={handleTypeChange}
            isDisabled={disabled}
            aria-label='Question type'
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        </div>

        <div className='flex items-center gap-2 shrink-0 mt-0.5'>
          <Checkbox
            isSelected={question.required}
            onChange={handleRequiredChange}
            isDisabled={disabled}
            label={
              <span className='text-sm text-[var(--color-text-secondary)]'>
                Required
              </span>
            }
          />
          {!disabled && (
            <Button
              size='sm'
              color='tertiary'
              iconLeading={XClose}
              onClick={handleRemove}
              aria-label={`Remove question ${index + 1}`}
            />
          )}
        </div>
      </div>

      {/* Options sub-editor — rendered only for select question types.
          pl-6 aligns sub-rows under the label input (drag handle 16px + gap 8px);
          in read-only mode the drag handle isn't rendered, so drop the indent. */}
      {isSelectType(question.type) && (
        <div className={cx('space-y-2', !disabled && 'pl-6')}>
          {question.options.map((opt, idx) => (
            <OptionInput
              key={optionIds[idx]}
              index={idx}
              value={opt}
              disabled={disabled}
              size={size}
              onChangeOption={handleOptionChange}
              onRemoveOption={handleRemoveOption}
            />
          ))}

          {optionsError && (
            <p
              id={optionsErrorId}
              className='text-xs text-[var(--color-text-error-primary)]'
              aria-live='polite'
            >
              Add at least one option
            </p>
          )}

          {!disabled && !atOptionMax && (
            <Button
              size='sm'
              color='tertiary'
              iconLeading={Plus}
              onClick={handleAddOption}
              aria-describedby={optionsError ? optionsErrorId : undefined}
            >
              Add option
            </Button>
          )}
        </div>
      )}

      {/* Contact property mapping — only for question types the backend can map.
          Same conditional indent as the options sub-editor above. */}
      {isMappableType(question.type) && (
        <div className={cx('space-y-1', !disabled && 'pl-6')}>
          <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>
            Save answer to a contact property (optional)
          </label>
          <ContactPropertyPicker
            value={question.map_to_property ?? null}
            onChange={handleMapToPropertyChange}
            disabled={disabled}
            size={size}
          />
          <p className='text-xs text-[var(--color-text-tertiary)]'>
            Only fills the property when it&apos;s empty — never overwrites
            existing contact data.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── BookingQuestionsInput ────────────────────────────────────────────────────

export const BookingQuestionsInput: FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  size = 'sm',
}) => {
  const questions = useMemo(() => parseBookingQuestions(value), [value]);
  const [announcement, setAnnouncement] = useState('');
  // Key of the question just added via "Add question" — its label input is
  // focused on mount so the host can start typing immediately.
  const [autoFocusKey, setAutoFocusKey] = useState<string | null>(null);

  const emit = useCallback(
    (next: BookingQuestion[]) => onChange(serializeBookingQuestions(next)),
    [onChange]
  );

  const sensors = useMultiValueSensors();

  const handleChangeQuestion = useCallback(
    (key: string, patch: Partial<BookingQuestion>) => {
      emit(questions.map(q => (q.key === key ? { ...q, ...patch } : q)));
    },
    [questions, emit]
  );

  const handleRemove = useCallback(
    (key: string) => {
      emit(questions.filter(q => q.key !== key));
    },
    [questions, emit]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = questions.findIndex(q => q.key === String(active.id));
        const newIndex = questions.findIndex(q => q.key === String(over.id));
        if (oldIndex !== -1 && newIndex !== -1) {
          emit(arrayMove(questions, oldIndex, newIndex));
          setAnnouncement(`Question moved to position ${newIndex + 1}`);
        }
      }
    },
    [questions, emit]
  );

  const handleAddQuestion = useCallback(() => {
    if (questions.length >= BOOKING_QUESTION_MAX.QUESTIONS) {
      return;
    }
    const key = generateQuestionKey(new Set(questions.map(q => q.key)));
    setAutoFocusKey(key);
    emit([
      ...questions,
      { key, label: '', type: 'short_text', required: false, options: [] },
    ]);
  }, [questions, emit]);

  const atMax = questions.length >= BOOKING_QUESTION_MAX.QUESTIONS;
  // One standing hint under the button carries both the cap and what answers
  // are used for, replacing the previous empty-state paragraph + separate
  // at-cap message. It is always rendered, so it can serve as the button's
  // accessible description whether or not the cap has been reached.
  const hintId = 'booking-questions-hint';

  return (
    <div
      data-component='booking-questions-editor'
      className='space-y-3'
      role='group'
      aria-label={propertyType.label ?? 'Booking questions'}
    >
      <div aria-live='polite' className='sr-only'>
        {announcement}
      </div>

      {questions.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={questions.map(q => q.key)}
            strategy={verticalListSortingStrategy}
          >
            <div className='space-y-2'>
              {questions.map((question, index) => (
                <QuestionRow
                  key={question.key}
                  question={question}
                  index={index}
                  disabled={disabled ?? false}
                  autoFocusLabel={question.key === autoFocusKey}
                  size={size}
                  onChangeQuestion={handleChangeQuestion}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className='flex flex-col items-start gap-1'>
        <Button
          size='sm'
          color='secondary'
          iconLeading={Plus}
          onClick={handleAddQuestion}
          isDisabled={disabled || atMax}
          aria-describedby={hintId}
        >
          Add question
        </Button>
        <p id={hintId} className='text-xs text-[var(--color-text-secondary)]'>
          Up to {BOOKING_QUESTION_MAX.QUESTIONS}. Answers show on the booking
          and in the confirmation email.
        </p>
      </div>
    </div>
  );
};
